import { useState, useMemo } from 'react';
import { Box, Chip, CircularProgress, Typography, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useParams } from 'react-router-dom';
import { SeoAudit, SeoAuditIssue, SeoAuditPage, IssueSeverity, IssueCategory, AuditStatus } from '@/types/api/seo';
import { PageHeader as SharedPageHeader } from '@/components/shared';
import Table, { Td, Th, Tr } from '@/trio-ui/Table';
import * as Styles from './SeoAuditDetail.styles';

type Props = {
  audit: SeoAudit | null;
  issues: SeoAuditIssue[];
  pages: SeoAuditPage[];
  isLoading: boolean;
  notFound: boolean;
  onResolveIssue: (issueId: string) => void;
};

const SEVERITY_CONFIG = {
  [IssueSeverity.CRITICAL]: { label: 'Errors', color: '#EF5B5B', description: 'Fix these first — they directly hurt rankings.' },
  [IssueSeverity.WARNING]: { label: 'Warnings', color: '#F4B400', description: 'Important issues that should be addressed.' },
  [IssueSeverity.INFO]: { label: 'Notices', color: '#4285F4', description: 'Minor improvements for best practice.' },
};

const CATEGORY_CONFIG: Record<string, { label: string; color: string }> = {
  [IssueCategory.TECHNICAL]: { label: 'Technical', color: '#EF5B5B' },
  [IssueCategory.CONTENT]: { label: 'Content', color: '#054AD9' },
  [IssueCategory.LINKS]: { label: 'Links', color: '#F4B400' },
  [IssueCategory.PERFORMANCE]: { label: 'Performance', color: '#0AB0A1' },
};

const ISSUE_TYPE_LABELS: Record<string, { title: string; fix: string }> = {
  http_error: { title: 'HTTP Errors (4xx)', fix: 'Restore the page, set up a 301 redirect, or remove broken links.' },
  server_error: { title: 'Server Errors (5xx)', fix: 'Check server logs and fix the root cause.' },
  missing_canonical: { title: 'Missing Canonical Tag', fix: 'Add <link rel="canonical"> to prevent duplicate content.' },
  noindex_tag: { title: 'Noindex Tag Detected', fix: 'Remove noindex if the page should appear in search.' },
  redirect_chain: { title: 'Redirect Chains', fix: 'Update links to point to the final URL directly.' },
  crawl_depth_deep: { title: 'Deep Pages (4+ clicks)', fix: 'Add internal links from higher-level pages.' },
  orphan_page: { title: 'Orphan Pages', fix: 'Add internal links from related pages to make them discoverable.' },
  missing_title: { title: 'Missing Title Tag', fix: 'Add a <title> tag (50-60 chars) with the target keyword.' },
  title_too_long: { title: 'Title Too Long (>60 chars)', fix: 'Shorten to under 60 characters to avoid truncation.' },
  title_too_short: { title: 'Title Too Short (<30 chars)', fix: 'Expand to 50-60 characters with relevant keywords.' },
  duplicate_title: { title: 'Duplicate Titles', fix: 'Give each page a unique title targeting different keywords.' },
  missing_h1: { title: 'Missing H1 Heading', fix: 'Add a single H1 that describes the page and includes the keyword.' },
  multiple_h1: { title: 'Multiple H1 Tags', fix: 'Keep one H1 per page. Convert extras to H2 or H3.' },
  thin_content: { title: 'Thin Content (<300 words)', fix: 'Add comprehensive content. Aim for 300+ words minimum.' },
  missing_meta_description: { title: 'Missing Meta Description', fix: 'Add a compelling meta description (120-160 chars).' },
  meta_description_too_long: { title: 'Meta Description Too Long', fix: 'Shorten to under 160 characters.' },
  missing_alt_text: { title: 'Images Missing Alt Text', fix: 'Add descriptive alt text to all images.' },
  no_internal_links: { title: 'No Internal Links', fix: 'Add internal links to related content on the site.' },
  slow_lcp: { title: 'Slow LCP (>2.5s)', fix: 'Optimize largest contentful paint — compress images, reduce server time.' },
  high_cls: { title: 'High CLS (>0.1)', fix: 'Set explicit dimensions on images/embeds to prevent layout shift.' },
  slow_load: { title: 'Slow Page Load (>3s)', fix: 'Reduce page size, optimize images, enable caching.' },
};

function getScoreColor(score: number): string {
  if (score >= 80) return '#0D9D58';
  if (score >= 50) return '#F4B400';
  return '#EF5B5B';
}

const SeoAuditDetailComponent = function ({
  audit,
  issues,
  pages,
  isLoading,
  notFound,
  onResolveIssue,
}: Props): JSX.Element {
  const { projectId = '' } = useParams();
  const basePath = `/seox/projects/${projectId}`;
  const [tab, setTab] = useState<'overview' | 'issues' | 'pages'>('overview');

  // Grouped data
  const severityCounts = useMemo(() => ({
    [IssueSeverity.CRITICAL]: issues.filter((i) => i.severity === IssueSeverity.CRITICAL).length,
    [IssueSeverity.WARNING]: issues.filter((i) => i.severity === IssueSeverity.WARNING).length,
    [IssueSeverity.INFO]: issues.filter((i) => i.severity === IssueSeverity.INFO).length,
  }), [issues]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const issue of issues) {
      counts[issue.category] = (counts[issue.category] || 0) + 1;
    }
    return counts;
  }, [issues]);

  const topIssues = useMemo(() => {
    const grouped = new Map<string, { type: string; severity: IssueSeverity; count: number; urls: string[] }>();
    for (const issue of issues) {
      if (!grouped.has(issue.issueType)) {
        grouped.set(issue.issueType, { type: issue.issueType, severity: issue.severity, count: 0, urls: [] });
      }
      const g = grouped.get(issue.issueType)!;
      g.count++;
      if (issue.affectedUrl && g.urls.length < 3) g.urls.push(issue.affectedUrl);
    }
    return [...grouped.values()].sort((a, b) => {
      const sevOrder = { critical: 0, warning: 1, info: 2 };
      if (sevOrder[a.severity] !== sevOrder[b.severity]) return sevOrder[a.severity] - sevOrder[b.severity];
      return b.count - a.count;
    });
  }, [issues]);

  const maxCategoryCount = Math.max(...Object.values(categoryCounts), 1);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#054AD9' }} />
      </Box>
    );
  }

  if (notFound || !audit) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">Audit not found.</Typography>
      </Box>
    );
  }

  // Running/queued state
  if (audit.status === AuditStatus.QUEUED || audit.status === AuditStatus.RUNNING) {
    return (
      <Box sx={{}}>
        <Styles.RunningState>
          <Styles.PulsingDot />
          <Typography variant="h4" fontWeight={500} sx={{ mb: 1 }}>
            {audit.status === AuditStatus.QUEUED ? 'Audit queued...' : 'Crawling your site...'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {audit.status === AuditStatus.RUNNING
              ? `Analyzing pages at crawl depth ${audit.crawlDepth}. This usually takes 30-60 seconds.`
              : 'The audit will start shortly.'}
          </Typography>
        </Styles.RunningState>
      </Box>
    );
  }

  const score = audit.healthScore ?? 0;
  const scoreColor = getScoreColor(score);
  const circumference = 2 * Math.PI * 60;
  const dashOffset = circumference - (score / 100) * circumference;

  return (
    <Box sx={{}}>
      <Styles.PageHeader>
        <Styles.Title>
          Site Audit — {new Date(audit.createdAt).toLocaleDateString()}
        </Styles.Title>
        <Chip
          label={audit.status === AuditStatus.COMPLETE ? 'Complete' : audit.status === AuditStatus.FAILED ? 'Failed' : audit.status}
          color={audit.status === AuditStatus.COMPLETE ? 'success' : audit.status === AuditStatus.FAILED ? 'error' : 'default'}
          size="small"
        />
      </Styles.PageHeader>

      {/* Tab navigation */}
      <Box sx={{ display: 'flex', gap: '4px', mb: '20px', borderBottom: '1px solid hsla(0,0%,0%,0.08)' }}>
        {(['overview', 'issues', 'pages'] as const).map((t) => (
          <Box
            key={t}
            onClick={() => setTab(t)}
            sx={{
              padding: '10px 16px',
              fontSize: 14,
              fontWeight: tab === t ? 500 : 400,
              color: tab === t ? '#054AD9' : '#71717A',
              borderBottom: `2px solid ${tab === t ? '#054AD9' : 'transparent'}`,
              cursor: 'pointer',
              marginBottom: '-1px',
              textTransform: 'capitalize',
              transition: 'color 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
              '&:hover': { color: '#054AD9' },
            }}
          >
            {t === 'overview' ? 'Overview' : t === 'issues' ? `All Issues (${issues.length})` : `Pages (${pages.length})`}
          </Box>
        ))}
      </Box>

      {/* ============ OVERVIEW TAB ============ */}
      {tab === 'overview' && (
        <>
          <Styles.OverviewGrid>
            {/* Left: Health Score + Severity Breakdown */}
            <Styles.HealthScoreCard>
              <Styles.ScoreRing $color={scoreColor}>
                <svg width="140" height="140">
                  <circle cx="70" cy="70" r="60" fill="none" stroke="#F4F4F5" strokeWidth="10" />
                  <circle
                    cx="70" cy="70" r="60" fill="none"
                    stroke={scoreColor} strokeWidth="10"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    strokeLinecap="round"
                  />
                </svg>
                <span className="score-value">{score}</span>
                <span className="score-label">Health Score</span>
              </Styles.ScoreRing>

              <Styles.SeverityBreakdown>
                {Object.entries(SEVERITY_CONFIG).map(([severity, config]) => (
                  <Styles.SeverityRow key={severity} $color={config.color}>
                    <Styles.SeverityDot $color={config.color} />
                    <Styles.SeverityLabel>{config.label}</Styles.SeverityLabel>
                    <Styles.SeverityCount $color={config.color}>
                      {severityCounts[severity as IssueSeverity] ?? 0}
                    </Styles.SeverityCount>
                  </Styles.SeverityRow>
                ))}
              </Styles.SeverityBreakdown>

              <Styles.ScoreSection style={{ marginBottom: 0, width: '100%' }}>
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="body2" fontWeight={500}>{audit.pagesCrawled}</Typography>
                  <Typography variant="caption" color="text.secondary">Pages</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="body2" fontWeight={500}>{audit.issuesFound}</Typography>
                  <Typography variant="caption" color="text.secondary">Issues</Typography>
                </Box>
              </Styles.ScoreSection>
            </Styles.HealthScoreCard>

            {/* Right: Categories + Top Issues */}
            <Styles.RightPanel>
              {/* Issues by Category */}
              <Styles.CategoryCard>
                <Styles.CategoryHeader>
                  <Styles.CategoryTitle>Issues by Category</Styles.CategoryTitle>
                </Styles.CategoryHeader>
                {Object.entries(CATEGORY_CONFIG).map(([cat, config]) => {
                  const count = categoryCounts[cat] ?? 0;
                  return (
                    <Styles.CategoryRow key={cat}>
                      <Styles.SeverityDot $color={config.color} />
                      <Styles.CategoryLabel>{config.label}</Styles.CategoryLabel>
                      <Styles.ProgressBar>
                        <Styles.ProgressFill $width={(count / maxCategoryCount) * 100} $color={config.color} />
                      </Styles.ProgressBar>
                      <Styles.IssueCount>{count}</Styles.IssueCount>
                    </Styles.CategoryRow>
                  );
                })}
              </Styles.CategoryCard>

              {/* Top Issues */}
              <Styles.TopIssuesCard>
                <Styles.CategoryHeader>
                  <Styles.CategoryTitle>Top Issues</Styles.CategoryTitle>
                  <Typography
                    variant="caption"
                    color="#054AD9"
                    sx={{ cursor: 'pointer', fontWeight: 500 }}
                    onClick={() => setTab('issues')}
                  >
                    View all →
                  </Typography>
                </Styles.CategoryHeader>
                {topIssues.slice(0, 10).map((issue) => {
                  const info = ISSUE_TYPE_LABELS[issue.type];
                  const sevConfig = SEVERITY_CONFIG[issue.severity];
                  return (
                    <Styles.IssueRow key={issue.type}>
                      <Styles.SeverityDot $color={sevConfig.color} />
                      <Styles.IssueInfo>
                        <Styles.IssueTitle>{info?.title ?? issue.type}</Styles.IssueTitle>
                        <Styles.IssueDescription>{info?.fix ?? ''}</Styles.IssueDescription>
                      </Styles.IssueInfo>
                      <Styles.IssueCount>{issue.count} URL{issue.count !== 1 ? 's' : ''}</Styles.IssueCount>
                    </Styles.IssueRow>
                  );
                })}
                {topIssues.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CheckCircleIcon sx={{ fontSize: 32, color: '#0D9D58', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No issues found!</Typography>
                  </Box>
                )}
              </Styles.TopIssuesCard>
            </Styles.RightPanel>
          </Styles.OverviewGrid>
        </>
      )}

      {/* ============ ALL ISSUES TAB ============ */}
      {tab === 'issues' && (
        <Table
          data={issues}
          renderHeader={
            <Tr>
              <Th width="90px">Severity</Th>
              <Th width="100px">Category</Th>
              <Th>Issue</Th>
              <Th>Affected URL</Th>
              <Th width="120px" textAlign="center">Action</Th>
            </Tr>
          }
          renderRow={(issue) => (
            <Tr key={issue.id}>
              <Td>
                <Chip
                  label={issue.severity}
                  color={issue.severity === IssueSeverity.CRITICAL ? 'error' : issue.severity === IssueSeverity.WARNING ? 'warning' : 'info'}
                  size="small"
                  sx={{ fontSize: 11 }}
                />
              </Td>
              <Td>
                <Typography variant="caption" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
                  {issue.category}
                </Typography>
              </Td>
              <Td>
                <Typography variant="body2" fontWeight={500}>
                  {ISSUE_TYPE_LABELS[issue.issueType]?.title ?? issue.issueType}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25, whiteSpace: 'pre-line' }}>
                  {issue.description}
                </Typography>
                {issue.recommendation && (
                  <Box sx={{ mt: 0.75, p: 1.5, backgroundColor: '#F4F4F5', borderRadius: '8px' }}>
                    <Typography variant="caption" color="text.primary" component="div" sx={{ whiteSpace: 'pre-line', fontWeight: 500 }}>
                      How to fix:
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ whiteSpace: 'pre-line', mt: 0.25 }}>
                      {issue.recommendation}
                    </Typography>
                  </Box>
                )}
              </Td>
              <Td>
                <Typography variant="caption" sx={{ wordBreak: 'break-all', color: 'text.secondary' }}>
                  {issue.affectedUrl ? new URL(issue.affectedUrl).pathname : '—'}
                </Typography>
              </Td>
              <Td textAlign="center">
                {issue.isResolved ? (
                  <Chip label="Resolved" color="success" size="small" variant="outlined" sx={{ fontSize: 11 }} />
                ) : (
                  <Button size="small" onClick={() => onResolveIssue(issue.id)} sx={{ fontSize: 12 }}>
                    Resolve
                  </Button>
                )}
              </Td>
            </Tr>
          )}
          renderEmptyState={
            <Tr>
              <Td colSpan={5} textAlign="center">
                <Box sx={{ py: 3 }}>
                  <CheckCircleIcon sx={{ fontSize: 32, color: '#0D9D58', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">No issues found — your site is healthy!</Typography>
                </Box>
              </Td>
            </Tr>
          }
        />
      )}

      {/* ============ PAGES TAB ============ */}
      {tab === 'pages' && (
        <Table
          data={pages}
          renderHeader={
            <Tr>
              <Th>URL</Th>
              <Th width="80px" textAlign="center">Status</Th>
              <Th>Title</Th>
              <Th width="90px" textAlign="center">Words</Th>
              <Th width="90px" textAlign="center">Load Time</Th>
              <Th width="70px" textAlign="center">H1s</Th>
              <Th width="80px" textAlign="center">Links</Th>
              <Th width="80px" textAlign="center">Images</Th>
            </Tr>
          }
          renderRow={(page) => (
            <Tr key={page.id}>
              <Td>
                <Typography variant="body2" fontWeight={500} sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={page.url}>
                  {new URL(page.url).pathname}
                </Typography>
              </Td>
              <Td textAlign="center">
                <Chip
                  label={page.statusCode ?? '—'}
                  color={page.statusCode === 200 ? 'success' : (page.statusCode ?? 0) >= 400 ? 'error' : 'warning'}
                  size="small"
                  sx={{ fontSize: 11, minWidth: 40 }}
                />
              </Td>
              <Td>
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                  {page.title || '—'}
                </Typography>
              </Td>
              <Td textAlign="center">
                <Typography variant="body2" color={page.wordCount != null && page.wordCount < 300 ? 'error.main' : 'text.primary'}>
                  {page.wordCount ?? '—'}
                </Typography>
              </Td>
              <Td textAlign="center">
                <Typography variant="body2" color={page.loadTimeMs != null && page.loadTimeMs > 3000 ? 'error.main' : 'text.primary'}>
                  {page.loadTimeMs != null ? `${page.loadTimeMs}ms` : '—'}
                </Typography>
              </Td>
              <Td textAlign="center">
                <Typography variant="body2" color={page.h1Count === 0 ? 'error.main' : page.h1Count != null && page.h1Count > 1 ? 'warning.main' : 'text.primary'}>
                  {page.h1Count ?? '—'}
                </Typography>
              </Td>
              <Td textAlign="center">
                <Typography variant="caption" color="text.secondary">
                  {page.internalLinks ?? 0}
                </Typography>
              </Td>
              <Td textAlign="center">
                <Typography variant="caption" color={page.imagesNoAlt != null && page.imagesNoAlt > 0 ? 'warning.main' : 'text.secondary'}>
                  {page.imagesTotal ?? 0}{page.imagesNoAlt ? ` (${page.imagesNoAlt} no alt)` : ''}
                </Typography>
              </Td>
            </Tr>
          )}
          renderEmptyState={
            <Tr>
              <Td colSpan={8} textAlign="center">No pages crawled yet.</Td>
            </Tr>
          }
        />
      )}
    </Box>
  );
};

export default SeoAuditDetailComponent;
